/**
 * Error state component for CCMS operations
 */

import { forwardRef } from 'react';
import { Card, Stack, Text, Title, Button, Group } from '@/components/ui';
import { IconAlertCircle, IconRefresh } from '@tabler/icons-react';
import { formatErrorForUser } from '../../utils/error-handling';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

export interface CCMSErrorStateProps {
  error: Error | string;
  title?: string;
  showRetry?: boolean;
  onRetry?: () => void;
  showDetails?: boolean;
  className?: string;
}

/**
 * Error state component for displaying errors gracefully
 *
 * @example
 * ```tsx
 * <CCMSErrorState
 *   error={error}
 *   title="Failed to load files"
 *   showRetry
 *   onRetry={() => refetch()}
 * />
 * ```
 */
export const CCMSErrorState = forwardRef<HTMLDivElement, CCMSErrorStateProps>(
  (props, ref) => {
    const {
      error,
      title = 'An error occurred',
      showRetry = true,
      onRetry,
      showDetails = false,
      className,
    } = props;

    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const errorMessage = typeof error === 'string' ? error : formatErrorForUser(error);
    const technicalMessage =
      typeof error === 'string' ? error : error.message;

    return (
      <Card ref={ref} variant="elevated" className={className} p="xl">
        <Stack gap="md" align="center">
          <IconAlertCircle size={48} color="var(--mantine-color-red-6)" />

          <Stack gap="xs" align="center">
            <Title order={3}>{title}</Title>
            <Text size="md" c="dimmed" ta="center" style={{ maxWidth: 500, ...accessibilityStyles }}>
              {errorMessage}
            </Text>
          </Stack>

          {showDetails && technicalMessage && (
            <Card variant="subtle" p="sm" style={{ maxWidth: 600 }}>
              <Text
                size="sm"
                c="dimmed"
                style={{ fontFamily: 'monospace', wordBreak: 'break-all', ...accessibilityStyles }}
              >
                {technicalMessage}
              </Text>
            </Card>
          )}

          {showRetry && onRetry && (
            <Button
              variant="gradient"
              gradientVariant="primary"
              leftSection={<IconRefresh size={16} />}
              onClick={onRetry}
              mt="sm"
            >
              Try Again
            </Button>
          )}
        </Stack>
      </Card>
    );
  }
);

CCMSErrorState.displayName = 'CCMSErrorState';

export default CCMSErrorState;
